import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import type { AgentBuilderNode, AgentBuilderEdge, AgentBuilderGraph, NodeData } from './agentBuilder.types';

interface AgentBuilderState {
  currentAgentBuilder?: AgentBuilderGraph;
  nodes: AgentBuilderNode[];
  edges: AgentBuilderEdge[];
  selectedNodeId?: string;
  isDirty: boolean;
}

export class AgentBuilderStore extends Store {
  static readonly storeName = 'sqAgentBuilderStore';
  persistenceLevel: PersistenceLevel = 'NONE';

  initialize(initializeMode: InitializeMode) {
    this.state = this.immutable({
      nodes: [],
      edges: [],
      isDirty: false,
    });
  }

  get currentAgentBuilder() {
    return this.state.get('currentAgentBuilder');
  }

  get nodes(): AgentBuilderNode[] {
    return this.state.get('nodes') || [];
  }

  get edges(): AgentBuilderEdge[] {
    return this.state.get('edges') || [];
  }

  get selectedNodeId() {
    return this.state.get('selectedNodeId');
  }

  get isDirty() {
    return this.state.get('isDirty') || false;
  }

  dehydrate() {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: AgentBuilderState) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    AGENT_BUILDER_SET_CURRENT: (payload: { agentBuilder?: AgentBuilderGraph }) => {
      this.state.set('currentAgentBuilder', payload.agentBuilder);
      if (payload.agentBuilder) {
        this.state.set('nodes', payload.agentBuilder.nodes);
        this.state.set('edges', payload.agentBuilder.edges);
      }
      const hasDraft = payload.agentBuilder?.meta?.hasDraft ?? false;
      this.state.set('isDirty', hasDraft);
    },

    AGENT_BUILDER_SET_NODES: (payload: { nodes: AgentBuilderNode[] }) => {
      const prevNodes = (this.state.get('nodes') || []) as AgentBuilderNode[];
      const prevCanonical = canonicalizeNodes(prevNodes);
      const nextCanonical = canonicalizeNodes(payload.nodes);
      const changed = JSON.stringify(prevCanonical) !== JSON.stringify(nextCanonical);
      this.state.set('nodes', payload.nodes);
      if (changed) {
        this.state.set('isDirty', true);
      }
    },

    AGENT_BUILDER_SET_EDGES: (payload: { edges: AgentBuilderEdge[] }) => {
      const prevEdges = (this.state.get('edges') || []) as AgentBuilderEdge[];
      const prevCanonical = canonicalizeEdges(prevEdges);
      const nextCanonical = canonicalizeEdges(payload.edges);
      const changed = JSON.stringify(prevCanonical) !== JSON.stringify(nextCanonical);
      this.state.set('edges', payload.edges);
      if (changed) {
        this.state.set('isDirty', true);
      }
    },

    AGENT_BUILDER_SET_SELECTED_NODE: (payload: { nodeId?: string }) => {
      this.state.set('selectedNodeId', payload.nodeId);
    },

    AGENT_BUILDER_SET_NAME: (payload: { name: string }) => {
      const current = this.state.get('currentAgentBuilder');
      if (current) {
        this.state.set('currentAgentBuilder', { ...current, name: payload.name });
      }
    },

    AGENT_BUILDER_UPDATE_NODE_DATA: (payload: { nodeId: string; data: Partial<NodeData> }) => {
      const nodes: AgentBuilderNode[] = this.nodes;
      const updatedNodes: AgentBuilderNode[] = nodes.map((node: AgentBuilderNode) =>
        node.id === payload.nodeId ? { ...node, data: { ...node.data, ...payload.data } } : node,
      );
      const prevCanonical = canonicalizeNodes(nodes);
      const nextCanonical = canonicalizeNodes(updatedNodes);
      const changed = JSON.stringify(prevCanonical) !== JSON.stringify(nextCanonical);
      this.state.set('nodes', updatedNodes);
      if (changed) {
        this.state.set('isDirty', true);
      }
    },

    AGENT_BUILDER_MARK_CLEAN: () => {
      this.state.set('isDirty', false);
    },

    AGENT_BUILDER_RESET: () => {
      this.state.set('currentAgentBuilder', undefined);
      this.state.set('nodes', []);
      this.state.set('edges', []);
      this.state.set('selectedNodeId', undefined);
      this.state.set('isDirty', false);
    },
  };
}

function canonicalizeNodes(
  nodes: AgentBuilderNode[],
): Array<{ id: string; type?: string; position?: { x?: number; y?: number }; data: unknown }> {
  return (nodes || []).map((n: AgentBuilderNode) => ({
    id: n.id,
    type: n.type,
    position: n.position ? { x: n.position.x, y: n.position.y } : undefined,
    data: n.data != null ? JSON.parse(JSON.stringify(n.data)) : undefined,
  }));
}

function canonicalizeEdges(edges: AgentBuilderEdge[]): Array<{
  id?: string;
  source: string;
  target: string;
  type?: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  data?: { edgeType?: string };
}> {
  return (edges || []).map((e: AgentBuilderEdge) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: e.type,
    sourceHandle: e.sourceHandle,
    targetHandle: e.targetHandle,
    data: e.data ? { edgeType: e.data.edgeType } : undefined,
  }));
}
